/**
 * Dummy Product Configurations - Matches Backend Expected Structure
 *
 * STRUCTURE: Each product contains:
 * - Product metadata (productId, productName, productTitle, productWeightage, etc.)
 * - Simplified productConfig object (mode, baseURL, httpMethod, requestBodySchema, uiConfig)
 *
 * PRODUCT CONFIG STRUCTURE (matches backend expectations):
 * {
 *   mode: "single" | "dual",
 *   baseURL: "complete-api-endpoint-url",
 *   httpMethod: "POST",
 *   requestBodySchema: [
 *     // For DUAL mode: side + image
 *     { fieldName: "side", source: "function", functionName: "getCurrentSide" },
 *     { fieldName: "image", source: "Upload", transform: "toBase64" }
 *
 *     // For SINGLE mode: image only
 *     { fieldName: "image", source: "Upload", transform: "toBase64" }
 *   ],
 *   uiConfig: {
 *     acceptFileTypes: "image/*,application/pdf,.tif,.tiff",
 *     maxFileSize: 5242880
 *   }
 * }
 *
 * SIDE FIELD BEHAVIOR:
 * - Only present in DUAL mode products
 * - Frontend automatically passes "front" or "back" based on active tab
 * - Single mode products don't have side field
 */

export const DUMMY_PRODUCT_DATA = {
  // Passport OCR - Dual Mode
  PCS_PR: {
    productId: '4ac21ac5-5cd9-4fa8-b298-08220876d29f',
    productName: 'Passport Reader',
    productKey: 'PCS_PR',
    productTitle: 'Passport Reader',
    productSubTitle: 'Scan and extract data from international passports',
    productAbout:
      'The Passport Reader allows you to scan and extract key information from passports quickly and accurately. Ideal for fast KYC and identity verification.',
    productWeightage: 0.7,
    pageWeightage: null,
    isActive: true,
    sequenceNumber: 1,
    productConfig: {
      mode: 'dual',
      baseURL: 'https://api-saas.pixl.ai/saas-passport',
      httpMethod: 'POST',
      requestBodySchema: [
        {
          fieldName: 'side',
          source: 'function',
          functionName: 'getCurrentSide',
        },
        {
          fieldName: 'image',
          source: 'Upload',
          transform: 'toBase64',
        },
        // {
        //   fieldName: 'image2',
        //   source: 'Upload',
        //   transform: 'toBase64',
        // },
      ],
      uiConfig: {
        acceptFileTypes: 'image/*,application/pdf,.tif,.tiff',
        maxFileSize: 5242880,
      },
    },
  },

  // Aadhaar OCR - Dual Mode
  PCS_AOCR: {
    productId: '5bc22bd6-6de0-4fb9-b399-08220876d29f',
    productName: 'Aadhaar Card OCR',
    productKey: 'PCS_AOCR',
    productTitle: 'Aadhaar Card OCR',
    productSubTitle: 'Extract data from Aadhaar cards with high accuracy',
    productAbout:
      'Aadhaar Card OCR enables quick extraction of information from Aadhaar cards for identity verification and KYC compliance in India.',
    productWeightage: 0.6,
    pageWeightage: null,
    isActive: true,
    sequenceNumber: 2,
    productConfig: {
      mode: 'dual',
      baseURL: 'https://api-saas.pixl.ai/saas-aadhaar',
      httpMethod: 'POST',
      requestBodySchema: [
        {
          fieldName: 'side',
          source: 'function',
          functionName: 'getCurrentSide',
        },
        {
          fieldName: 'image',
          source: 'Upload',
          transform: 'toBase64',
        },
      ],
      uiConfig: {
        acceptFileTypes: 'image/*,application/pdf,.tif,.tiff',
        maxFileSize: 5242880,
      },
    },
  },

  // PAN Card OCR - Single Mode
  PCS_PCOCR: {
    productId: '6cd33ce7-7ef1-4fc0-b49a-08220876d29f',
    productName: 'PAN Card OCR',
    productKey: 'PCS_PCOCR',
    productTitle: 'PAN Card OCR',
    productSubTitle: 'Digitize PAN card information instantly',
    productAbout:
      'PAN Card OCR extracts details from Permanent Account Number cards for tax verification and financial services in India.',
    productWeightage: 0.5,
    pageWeightage: null,
    isActive: true,
    sequenceNumber: 3,
    productConfig: {
      mode: 'single',
      baseURL: 'https://api-saas.pixl.ai/saas-pancard/pan1',
      httpMethod: 'POST',
      requestBodySchema: [
        {
          fieldName: 'image',
          source: 'Upload',
          transform: 'toBase64',
        },
      ],
      uiConfig: {
        acceptFileTypes: 'image/*,application/pdf,.tif,.tiff',
        maxFileSize: 5242880,
      },
    },
  },

  // Emirates ID OCR - Dual Mode
  PCS_EIDOCR: {
    productId: '7de44df8-8fg2-4gd1-c5ab-08220876d29f',
    productName: 'Emirates ID OCR',
    productKey: 'PCS_EIDOCR',
    productTitle: 'Emirates ID OCR',
    productSubTitle: 'Extract data from UAE Emirates ID cards',
    productAbout:
      'Emirates ID OCR processes UAE identity cards for quick verification and data extraction in the Middle East region.',
    productWeightage: 0.65,
    pageWeightage: null,
    isActive: true,
    sequenceNumber: 4,
    productConfig: {
      mode: 'dual',
      baseURL: 'https://api-saas.pixl.ai/saas-emiratesid',
      httpMethod: 'POST',
      requestBodySchema: [
        {
          fieldName: 'side',
          source: 'function',
          functionName: 'getCurrentSide',
        },
        {
          fieldName: 'image',
          source: 'Upload',
          transform: 'toBase64',
        },
      ],
      uiConfig: {
        acceptFileTypes: 'image/*,application/pdf,.tif,.tiff',
        maxFileSize: 5242880,
      },
    },
  },

  // Aadhaar Masking - Single Mode
  PCS_AMASK: {
    productId: '8ef55eg9-9gh3-4he2-d6bc-08220876d29f',
    productName: 'Aadhaar Masking',
    productKey: 'PCS_AMASK',
    productTitle: 'Aadhaar Masking',
    productSubTitle: 'Secure Aadhaar card data with intelligent masking',
    productAbout:
      'Aadhaar Masking protects sensitive information on Aadhaar cards by intelligently masking numbers while maintaining document readability.',
    productWeightage: 0.4,
    pageWeightage: 0.15,
    isActive: true,
    sequenceNumber: 5,
    productConfig: {
      mode: 'single',
      baseURL: 'https://api-saas.pixl.ai/saas-aadhaar-mask/api/v7/aadharMask',
      httpMethod: 'POST',
      requestBodySchema: [
        {
          fieldName: 'image',
          source: 'Upload',
          transform: 'toBase64',
        },
      ],
      uiConfig: {
        acceptFileTypes: 'image/*,application/pdf,.tif,.tiff',
        maxFileSize: 5242880,
      },
    },
  },

  // Voter ID OCR - Dual Mode
  PCS_VIDOCR: {
    productId: '9fg66fh0-0hi4-4if3-e7cd-08220876d29f',
    productName: 'Voter ID OCR',
    productKey: 'PCS_VIDOCR',
    productTitle: 'Voter ID OCR',
    productSubTitle: 'Extract data from Indian Voter ID cards',
    productAbout:
      'Voter ID OCR processes Indian Election Commission voter identity cards for verification and data extraction.',
    productWeightage: 0.6,
    pageWeightage: null,
    isActive: true,
    sequenceNumber: 6,
    productConfig: {
      mode: 'dual',
      baseURL: 'https://api-saas.pixl.ai/saas-voterid',
      httpMethod: 'POST',
      requestBodySchema: [
        {
          fieldName: 'side',
          source: 'function',
          functionName: 'getCurrentSide',
        },
        {
          fieldName: 'image',
          source: 'Upload',
          transform: 'toBase64',
        },
      ],
      uiConfig: {
        acceptFileTypes: 'image/*,application/pdf,.tif,.tiff',
        maxFileSize: 5242880,
      },
    },
  },

  // Malaysian Voter ID OCR - Single Mode
  PCS_MVIDOCR: {
    productId: '0gh77gi1-1ij5-4jg4-f8de-08220876d29f',
    productName: 'Malaysian Voter ID OCR',
    productKey: 'PCS_MVIDOCR',
    productTitle: 'Malaysian Voter ID OCR',
    productSubTitle: 'Process Malaysian voter identification cards',
    productAbout:
      'Malaysian Voter ID OCR extracts information from Malaysian MyKad identity cards for verification purposes.',
    productWeightage: 0.65,
    pageWeightage: null,
    isActive: true,
    sequenceNumber: 7,
    productConfig: {
      mode: 'single',
      baseURL: 'https://api-saas.pixl.ai/saas-malaysian-voter/MalaysianId',
      httpMethod: 'POST',
      requestBodySchema: [
        {
          fieldName: 'image',
          source: 'Upload',
          transform: 'toBase64',
        },
      ],
      uiConfig: {
        acceptFileTypes: 'image/*,application/pdf,.tif,.tiff',
        maxFileSize: 5242880,
      },
    },
  },

  // Malaysian Driving License OCR - Single Mode
  PCS_MDLOCR: {
    productId: '1hi88hj2-2jk6-4kh5-g9ef-08220876d29f',
    productName: 'Malaysian DL OCR',
    productKey: 'PCS_MDLOCR',
    productTitle: 'Malaysian Driving License OCR',
    productSubTitle: 'Extract data from Malaysian driving licenses',
    productAbout:
      'Malaysian DL OCR processes Malaysian driving licenses for quick verification and data extraction.',
    productWeightage: 0.6,
    pageWeightage: null,
    isActive: true,
    sequenceNumber: 8,
    productConfig: {
      mode: 'single',
      baseURL: 'https://devapi.pixl.ai/pcs6/MalaysianDL',
      httpMethod: 'POST',
      requestBodySchema: [
        {
          fieldName: 'image',
          source: 'Upload',
          transform: 'toBase64',
        },
      ],
      uiConfig: {
        acceptFileTypes: 'image/*,application/pdf,.tif,.tiff',
        maxFileSize: 5242880,
      },
    },
  },

  // Mexican ID OCR - Dual Mode
  PCS_MIDOCR: {
    productId: '2ij99ik3-3kl7-4li6-h0fg-08220876d29f',
    productName: 'Mexican ID OCR',
    productKey: 'PCS_MIDOCR',
    productTitle: 'Mexican Voter ID OCR',
    productSubTitle: 'Process Mexican voter identification cards',
    productAbout:
      'Mexican ID OCR extracts data from INE (Instituto Nacional Electoral) cards for identity verification in Mexico.',
    productWeightage: 0.65,
    pageWeightage: null,
    isActive: true,
    sequenceNumber: 9,
    productConfig: {
      mode: 'dual',
      baseURL: 'https://api-saas.pixl.ai/saas-mexican-id',
      httpMethod: 'POST',
      requestBodySchema: [
        {
          fieldName: 'side',
          source: 'function',
          functionName: 'getCurrentSide',
        },
        {
          fieldName: 'image',
          source: 'Upload',
          transform: 'toBase64',
        },
      ],
      uiConfig: {
        acceptFileTypes: 'image/*,application/pdf,.tif,.tiff',
        maxFileSize: 5242880,
      },
    },
  },

  // UAE Visa OCR - Single Mode
  PCS_UVOCR: {
    productId: '3jk00jl4-4lm8-4mj7-i1gh-08220876d29f',
    productName: 'UAE Visa OCR',
    productKey: 'PCS_UVOCR',
    productTitle: 'UAE Visa OCR',
    productSubTitle: 'Extract data from UAE visa documents',
    productAbout:
      'UAE Visa OCR processes United Arab Emirates visa documents for quick verification and data extraction.',
    productWeightage: 0.7,
    pageWeightage: null,
    isActive: true,
    sequenceNumber: 10,
    productConfig: {
      mode: 'single',
      baseURL: 'https://api-saas.pixl.ai/saas-uae-visa/UAEVisa',
      httpMethod: 'POST',
      requestBodySchema: [
        {
          fieldName: 'image',
          source: 'Upload',
          transform: 'toBase64',
        },
      ],
      uiConfig: {
        acceptFileTypes: 'image/*,application/pdf,.tif,.tiff',
        maxFileSize: 5242880,
      },
    },
  },
};

/**
 * Get complete dummy product data by product key
 * This includes ALL fields from the API response PLUS the productConfig object
 *
 * @param {string} productKey - The product key (e.g., 'PCS_PR')
 * @returns {Object|null} - Complete product data with embedded productConfig, or null if not found
 */
export const getDummyProductData = (productKey) => {
  return DUMMY_PRODUCT_DATA[productKey] || null;
};

/**
 * Get only the productConfig part from dummy data
 * (For backward compatibility)
 *
 * @param {string} productKey - The product key (e.g., 'PCS_PR')
 * @returns {Object|null} - The product configuration or null if not found
 */
export const getDummyProductConfig = (productKey) => {
  const productData = DUMMY_PRODUCT_DATA[productKey];
  return productData ? productData.productConfig : null;
};
